// deps
import React, { memo, ReactElement, useCallback, useMemo } from "react";
import classNames from "classnames";

// components
import { InputFieldv4, InputFieldProps } from "@swiggy-private/core-ui";

import styles from "../AddNewCard.v4.scss";

export interface InputProps extends InputFieldProps {
    showError: boolean;
    errorLabel: string;
    maxLength: number;
    name: string;
    type: string;
    onChange: (value: string, name?: string) => void;
    showWarning: boolean;
    warningLabel: string;
}

const EMPTY_FUNC: VoidFunction = () => void 0;

function Input(props: InputProps): ReactElement {
    const _onChange = props.onChange;
    const onChange: InputFieldProps["onChange"] = useCallback(
        (value, name): void => {
            if (value.toString().length > props.maxLength) {
                return;
            }

            _onChange(value, name);
        },
        [_onChange, props.maxLength],
    );

    const attrs = useMemo(
        () => ({
            type: props.type,
            name: props.name,
            maxLength: props.maxLength,
            ...props.attrs,
        }),
        [props.attrs, props.maxLength, props.name, props.type],
    );

    return (
        <InputFieldv4
            value={props.value}
            label={props.label}
            onChange={onChange}
            onBlur={props.onBlur}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            attrs={attrs}
            error={props.showError}
            className={styles.newCardInputContainer}
            inputClassName={classNames(styles.newCardInput, {
                [styles.newCardInputDisabled]: props.disabled,
            })}
            setSelectionRange={props.setSelectionRange}
            errorMessage={props.showError ? props.errorLabel : undefined}
            keepLabelActive={props.keepLabelActive}
            disabled={props.disabled}
            warningMessage={props.showWarning ? props.warningLabel : undefined}
            warning={props.showWarning}
        />
    );
}

Input.defaultProps = {
    errorLabel: "",
    onBlur: EMPTY_FUNC,
    setSelectionRange: false,
    showError: false,
    style: null,
    disabled: false,
    error: false,
    inline: false,
    scrollIntoView: false,
    showWarning: false,
    warningLabel: "",
};

export default memo(Input);
